export interface UnitContent {
  numero: number;
  titulo: string;
  resumen: string;
  conceptosClave: Array<{ titulo?: string; descripcion: string }>;
  ejemploAplicado: string;
}

export const unitsContent: UnitContent[] = [
  {
    numero: 1,
    titulo: "Introducción al Mejoramiento Genético Vegetal",
    resumen: "Esta unidad introduce los conceptos fundamentales del mejoramiento genético vegetal, destacando su importancia para incrementar la productividad agrícola y garantizar la seguridad alimentaria mundial. Se analiza el impacto histórico de la Revolución Verde y las implicaciones de la transformación agrícola moderna.",
    conceptosClave: [
      {
        titulo: "Importancia del mejoramiento",
        descripcion: "Los incrementos en productividad agrícola resultan del amplio uso de modernas variedades de plantas desarrolladas mediante mejoramiento genético."
      },
      {
        titulo: "Conservación ambiental",
        descripcion: "El uso adecuado de recursos genéticos es esencial para mantener un ambiente no contaminado para futuras generaciones."
      },
      {
        titulo: "La Revolución Verde",
        descripcion: "Ejemplo histórico del impacto del mejoramiento genético en la adopción y reemplazo de poblaciones vegetales por nuevos cultivares de alto rendimiento."
      },
      {
        titulo: "Procesos técnicos y legales",
        descripcion: "El desarrollo y diseminación de nuevos cultivares involucra aspectos científicos, legales y políticos complejos."
      }
    ],
    ejemploAplicado: "Consideremos el caso del trigo enano desarrollado por Norman Borlaug durante la Revolución Verde. Estos cultivares de porte bajo (50-100 cm) resistían mejor el vuelco bajo fertilización nitrogenada intensa, permitiendo rendimientos de 5-7 ton/ha comparados con 2-3 ton/ha de variedades tradicionales. Esta innovación, producto del cruzamiento entre variedades mexicanas adaptadas y líneas japonesas de porte bajo (gen Rht), transformó la agricultura de México, India y Pakistán entre 1960-1970, evitando hambrunas masivas."
  },
  {
    numero: 2,
    titulo: "Etapas en un Programa de Mejoramiento Genético",
    resumen: "El desarrollo de una nueva variedad es un proceso complejo, costoso y que demanda largos periodos (8-12 años en promedio). Esta unidad describe las etapas sistemáticas que componen un programa de mejoramiento, desde el establecimiento de objetivos hasta la liberación comercial del cultivar.",
    conceptosClave: [
      {
        titulo: "1. Establecimiento de objetivos",
        descripcion: "Definir metas claras basadas en necesidades del mercado, limitantes ambientales y requerimientos de productores (ej. aumento de rendimiento, resistencia a enfermedades, calidad nutricional)."
      },
      {
        titulo: "2. Generación de variabilidad",
        descripcion: "Crear diversidad genética mediante cruzamientos, mutaciones inducidas o uso de germoplasma exótico."
      },
      {
        titulo: "3. Selección",
        descripcion: "Identificar y aislar genotipos superiores a través de múltiples generaciones y ambientes."
      },
      {
        titulo: "4. Evaluación",
        descripcion: "Ensayos comparativos rigurosos para validar el desempeño del nuevo cultivar versus testigos comerciales."
      },
      {
        titulo: "5. Multiplicación y diseminación",
        descripcion: "Producción de semilla genética, básica y comercial para liberar el cultivar al mercado."
      }
    ],
    ejemploAplicado: "En un programa de mejoramiento de maíz para resistencia a sequía: Año 1-2: Identificación de germoplasma tolerante (ej. razas andinas), cruzamientos iniciales. Año 3-6: Autofecundaciones sucesivas (S0→S6) y selección bajo estrés hídrico controlado, evaluando stay-green, senescencia foliar retrasada (ASI < 3 días). Año 7-9: Evaluación de aptitud combinatoria general mediante top-cross, formación de híbridos experimentales. Año 10-12: Ensayos multiambientales en zonas semiáridas, registro INASE, multiplicación de semilla híbrida."
  },
  {
    numero: 3,
    titulo: "Sistemas de Reproducción de las Plantas Cultivadas",
    resumen: "El conocimiento de los mecanismos reproductivos de una especie es fundamental para planear convenientemente un programa de mejora. Los sistemas de reproducción determinan la estructura genética de las poblaciones y condicionan las estrategias de manipulación del material vegetal.",
    conceptosClave: [
      {
        titulo: "Autógamas",
        descripcion: ">95% autopolinización. Ejemplos: trigo, arroz, cebada, soja, tomate, poroto. Poblaciones naturales altamente homocigotas. Métodos: pedigrí, genealógico, descendencia de semilla única (SSD)."
      },
      {
        titulo: "Alógamas",
        descripcion: ">50% fecundación cruzada. Ejemplos: maíz, girasol, alfalfa, centeno, cebolla. Poblaciones heterocigotas, depresión endogámica. Métodos: selección recurrente, híbridos, variedades sintéticas."
      },
      {
        titulo: "Intermedias o parcialmente alógamas",
        descripcion: "10-50% polinización cruzada. Ejemplos: algodón, sorgo. Estrategias mixtas de mejoramiento."
      },
      {
        titulo: "Asexuales o apomícticas",
        descripcion: "Reproducción vegetativa o por semilla apomíctica. Ejemplos: papa, caña de azúcar, frutilla, yuca, algunos pastos. Mantienen genotipo heterocigoto intacto."
      }
    ],
    ejemploAplicado: "Comparación de estrategias en maíz (alógama) vs. trigo (autógama): Maíz: Sistema de polinización anemófila con flores imperfectas separadas (monoico). La separación espacial espiga/panojas y protandria favorecen alogamia. Programas comerciales desarrollan híbridos simples (L1×L2) a partir de líneas S7-S8, explotando heterosis de 20-40%. Trigo: Flores perfectas cleistógamas (autopolinización antes de apertura floral). Programas desarrollan líneas puras mediante método genealógico: F1→F2 (selección individual)→F3-F5 (evaluación progenies)→F6-F8 (ensayos multiambientales). No requiere aislamiento para mantener pureza varietal."
  },
  {
    numero: 4,
    titulo: "Generación de Variabilidad",
    resumen: "La variabilidad genética es primordial para cualquier programa de mejoramiento vegetal. Esta unidad analiza las fuentes naturales de variación genética y los métodos para generar variabilidad artificial, ampliando la base genética disponible para la selección.",
    conceptosClave: [
      {
        titulo: "Recursos genéticos",
        descripcion: "Landraces, especies silvestres relacionadas, cultivares obsoletos y germoplasma conservado que mantienen alelos de interés agronómico."
      },
      {
        titulo: "Hibridación",
        descripcion: "Cruzamiento entre genotipos diferentes para generar segregación y recombinación genética en generaciones F2."
      },
      {
        titulo: "Mutaciones",
        descripcion: "Cambios hereditarios en la secuencia de ADN inducidas por radiación, agentes químicos o espontáneas."
      },
      {
        titulo: "Poliploidía",
        descripcion: "Duplicación de cromosomas generando autotetraploides, alotetraploides y su potencial para heterosis."
      },
      {
        titulo: "Variación somaclonal",
        descripcion: "Variantes generadas durante cultivo de tejidos y regeneración de plantas."
      },
      {
        titulo: "Ingeniería genética",
        descripcion: "Introducción directa de genes mediante técnicas de transformación."
      }
    ],
    ejemploAplicado: "La introgresión de alelos de resistencia en tomate (Solanum lycopersicum) desde especies silvestres relacionadas es un excelente ejemplo de generación de variabilidad. S. pimpinellifolium y S. peruvianum poseen genes de resistencia a Phytophthora infestans (tizón tardío), Fusarium y Verticillium. Mediante cruzamientos sucesivos y retrocruza, estos alelos fueron introducidos en cultivares comerciales. Por ejemplo, el gen Ph-3 de resistencia a Phytophthora fue introgresado desde S. pimpinellifolium, permitiendo el desarrollo de variedades de tomate resistentes al tizón tardío, reduciendo significativamente la incidencia de la enfermedad y el uso de fungicidas en cultivos comerciales."
  },
  {
    numero: 5,
    titulo: "Herencia Cuantitativa",
    resumen: "La mayoría de los caracteres de importancia económica en plantas cultivo son de naturaleza cuantitativa, controlados por múltiples genes con aditividad en sus efectos. Esta unidad examina los principios de herencia cuantitativa, los componentes de varianza y la distribución normal de caracteres fenotípicos.",
    conceptosClave: [
      {
        titulo: "Modelo aditivo",
        descripcion: "P = G + A + GxA, donde el fenotipo es resultado de efectos genéticos, ambientales y su interacción."
      },
      {
        titulo: "Componentes de varianza genética",
        descripcion: "Varianza aditiva (VA), varianza de dominancia (VD), varianza epistática (VI)."
      },
      {
        titulo: "Varianza ambiental",
        descripcion: "VE, representa la variación causada por factores del ambiente."
      },
      {
        titulo: "Distribución normal",
        descripcion: "Los caracteres cuantitativos tienden a presentar distribución normal en poblaciones grandes."
      },
      {
        titulo: "QTLs",
        descripcion: "Loci de caracteres cuantitativos identificables mediante mapeo molecular."
      }
    ],
    ejemploAplicado: "La altura de planta en trigo (Triticum aestivum) es un carácter cuantitativo controlado por múltiples genes. Los genes Rht (reduced height) principales son el Rht-B1 y Rht-D1, pero más de 30 QTLs adicionales afectan esta característica. El cálculo de varianza se realiza partiendo de datos de progenie: si una población F2 presenta altura media de 85 cm con varianza fenotípica total de 36 cm², la heredabilidad puede estimarse comparando familias parentales (P = 70 y 100 cm) versus F1 y F2. Este análisis permite identificar cuales componentes de varianza (aditiva, dominancia) predominan en la herencia de altura."
  },
  {
    numero: 6,
    titulo: "Heredabilidad",
    resumen: "La heredabilidad es la proporción de variación fenotípica atribuible a causas genéticas. Es un concepto clave que actúa como predictor de la respuesta a la selección.",
    conceptosClave: [
      {
        descripcion: "Heredabilidad sentido amplio (H²): H² = VG / VP, representa la proporción total de varianza genética sobre varianza fenotípica total."
      },
      {
        descripcion: "Heredabilidad sentido estricto (h²): h² = VA / VP, considera solo la varianza aditiva, que es la que responde efectivamente a la selección."
      },
      {
        descripcion: "Rangos interpretativos: Alta heredabilidad (h² > 0.6), heredabilidad moderada (0.3-0.6), baja heredabilidad (h² < 0.3)."
      },
      {
        descripcion: "Ecuación fundamental: La respuesta a selección (R) es directamente proporcional a la heredabilidad e intensidad de selección."
      },
      {
        descripcion: "Métodos de estimación: Comparación padres-progenie, análisis familial, análisis de varianza."
      }
    ],
    ejemploAplicado: "El contenido de proteína en trigo es un carácter de importancia agronómica y nutricional. En un programa de mejoramiento, se evaluó heredabilidad del contenido proteico en una población derivada de cruzamiento entre cultivar alto-proteína (14%) y bajo-proteína (9%). El análisis de varianza de 100 líneas F6 mostró varianza genética (VG) = 0.8 y varianza fenotípica total (VP) = 2.4. Con estos valores: h² = 0.8/2.4 = 0.33 (heredabilidad moderada). Mediante ecuación R = h² × S, con intensidad selección (S) de 1.0%, la ganancia esperada por ciclo fue 0.33% en contenido proteico, permitiendo predicción de respuesta a selección para futuras generaciones."
  },
  {
    numero: 7,
    titulo: "Selección",
    resumen: "La selección es el proceso central del mejoramiento genético que provoca cambios en las frecuencias génicas de las poblaciones. Esta unidad examina los diferentes métodos de selección y cómo calcular y maximizar la intensidad de selección para obtener mayores ganancias genéticas.",
    conceptosClave: [
      {
        descripcion: "Ecuación fundamental de selección: R = h² × S, donde R es la respuesta, h² la heredabilidad y S la intensidad selección."
      },
      {
        descripcion: "Selección masal: Selección simple basada en fenotipo individual, aplicable a caracteres de alta heredabilidad."
      },
      {
        descripcion: "Selección familiar: Considera rendimiento de familias completas, útil para caracteres de baja heredabilidad individual."
      },
      {
        descripcion: "Selección por índices: Combina múltiples caracteres mediante ponderaciones para optimizar respuesta multivariada."
      },
      {
        descripcion: "Intensidad de selección: Determinada por proporción de individuos seleccionados, más estricta selección = mayor intensidad."
      }
    ],
    ejemploAplicado: "En un programa de selección recurrente para rendimiento en maíz, se evaluaron 500 plantas en generación F2. El rendimiento medio fue 8 ton/ha con desviación estándar de 1.2 ton/ha. Se seleccionaron las mejores 50 plantas (10% superior) con rendimiento promedio de 10 ton/ha. La intensidad selección fue S = (10 - 8) = 2 ton/ha. Con heredabilidad estimada h² = 0.4, la respuesta esperada fue R = 0.4 × 2 = 0.8 ton/ha. En la generación siguiente, el rendimiento promedio aumentó a 8.8 ton/ha, validando la predicción. Este incremento de 0.8 ton/ha por ciclo selectivo es la ganancia genética esperada en el mejoramiento por selección recurrente."
  },
  {
    numero: 8,
    titulo: "Endocría y Heterosis",
    resumen: "La endocría genera homocigosis progresiva en líneas puras, reduciendo vigor y productividad (depresión endogámica). La heterosis, en cambio, es el vigor híbrido observado en F1 de cruzas entre parentales genéticamente distantes.",
    conceptosClave: [
      {
        descripcion: "Coeficiente de endocría (F): Mide el grado de homocigosis, aumenta 50% por generación en autofecundación."
      },
      {
        descripcion: "Depresión endogámica: Reducción de vigor fenotípico causada por exposición de alelos deletéreos recesivos."
      },
      {
        descripcion: "Heterosis o vigor híbrido: Superioridad del F1 respecto a la media parental."
      },
      {
        descripcion: "Heterosis de dominancia: Máscara de alelos deletéreos recesivos en heterocigosis."
      },
      {
        descripcion: "Heterosis de sobredominancia: Heterocigoto superior al mejor homocigoto."
      }
    ],
    ejemploAplicado: "En el desarrollo de híbridos simples de maíz, se obtuvieron líneas endocriadas mediante autofecundación sucesiva durante 7 generaciones (S7). Dos líneas parentales mostraban: Línea A rendimiento 4 ton/ha, Línea B rendimiento 5 ton/ha, promedio parental = 4.5 ton/ha. El híbrido simple F1 (A×B) produjo 8.55 ton/ha. La heterosis fue calculada como [(8.55 - 4.5) / 4.5] × 100 = 90%, indicando vigor híbrido excepcional. En maíz, heterosis de 190% o mayor es común cuando los parentales son genéticamente distantes. Este vigor híbrido es la base de la producción comercial de híbridos de maíz."
  },
  {
    numero: 9,
    titulo: "Métodos de Mejoramiento en Alógamas",
    resumen: "En plantas alógamas (polinización cruzada), el objetivo es aumentar la frecuencia de alelos favorables mientras se mantiene la heterocigosis para explotar heterosis.",
    conceptosClave: [
      {
        descripcion: "Selección masal: Método simple pero efectivo de selección continua sin control de cruzamientos."
      },
      {
        descripcion: "Prueba de progenie: Evaluación de plantas candidatas mediante rendimiento de descendencia en generación siguiente."
      },
      {
        descripcion: "Selección recurrente: Ciclos repetidos de selección para aumentar frecuencia alelos favorables."
      },
      {
        descripcion: "ACG (Aptitud Combinatoria General): Estimación del valor de línea en cruzamientos específicos."
      },
      {
        descripcion: "ACE (Aptitud Combinatoria Específica): Efectos particulares de combinaciones parentales específicas."
      },
      {
        descripcion: "Variedades sintéticas: Población desarrollada de líneas puras seleccionadas intercuzadas."
      }
    ],
    ejemploAplicado: "Un programa de selección recurrente en alfalfa (Medicago sativa, alógama) buscaba mejorar resistencia a Therioaphis maculata (pulgón de la alfalfa). Se seleccionaron plantas con antixenosis (no preferencia) y tolerancia. Mediante cinco ciclos de selección recurrente (SRR), se aumentó la frecuencia de alelos de resistencia. En el ciclo 0, incidencia de daño fue 85%, en ciclo 5 fue 30%. La ganancia genética acumulada fue 55% en reducción de daño. Debido a la alogamia obligada, la población mantuvo diversidad genética y heterocigosis, evitando depresión endogámica. El cultivar final fue lanzado como variedad sintética derivada de 12 plantas élites intercuzadas, combinando resistencia mejorada con vigor híbrido."
  },
  {
    numero: 10,
    titulo: "Cultivares Híbridos",
    resumen: "Un cultivar híbrido es el F1 resultante del cruzamiento entre dos o más parentales diferentes, generalmente líneas endocriadas. Los híbridos explotan heterosis para obtener incrementos significativos de rendimiento.",
    conceptosClave: [
      {
        descripcion: "Líneas parentales: Generalmente líneas endocriadas (homocigotas) derivadas de selección recurrente o retrocruzas."
      },
      {
        descripcion: "Aptitud Combinatoria General (ACG): Valor de línea en promedio de cruzamientos, predice capacidad de contribuir alelos favorables."
      },
      {
        descripcion: "Aptitud Combinatoria Específica (ACE): Desviaciones de la predicción ACG para cruzamiento particular."
      },
      {
        descripcion: "Tipos de híbridos: Simple (F1 de dos líneas), triple (F1 × línea), doble (F1 × F1)."
      },
      {
        descripcion: "Predicción de rendimiento: Rendimiento F1 ≈ media parentales + heterosis + efectos específicos de combinación."
      }
    ],
    ejemploAplicado: "En un programa de mejoramiento de maíz para desarrollo de híbridos dobles, se evaluaron 4 líneas parentales (A, B, C, D) en diseño factorial completo. Se formaron dos híbridos simples intermedios: AB (rendimiento 7.5 ton/ha) y CD (rendimiento 7.2 ton/ha), con promedio = 7.35 ton/ha. El híbrido doble (AB × CD) produjo 9.8 ton/ha. La heterosis del doble fue calculada como [(9.8 - 7.35) / 7.35] × 100 = 33%. Este incremento respecto a la media de híbridos simples demuestra la eficiencia de híbridos dobles. El análisis de ACG identificó que línea A tenía ACG = +0.5, línea B = +0.3, línea C = -0.2, línea D = -0.6, permitiendo predicción precisa de rendimiento doble antes de su evaluación fenotípica."
  },
  {
    numero: 11,
    titulo: "Mejoramiento en Autógamas",
    resumen: "En plantas autógamas (autofecundación), el objetivo es desarrollar líneas puras homocigotas que sean superiores. Esta unidad examina los principales métodos de mejoramiento utilizados en autógamas, incluyendo método genealógico, SSD (Single Seed Descent), bulk-pedigrí y retrocruzas.",
    conceptosClave: [
      {
        descripcion: "Método genealógico (pedigrí): Selección individual en cada generación F2, F3, F4 hasta F6/F7, evaluación fenotípica continua."
      },
      {
        descripcion: "Single Seed Descent (SSD): Avance rápido de generaciones (2-3 por año) sin selección, mínimo laboreo."
      },
      {
        descripcion: "Bulk-pedigrí: Combinación de métodos, bulk en F2-F5, pedigrí en F6 adelante."
      },
      {
        descripcion: "Retrocruza: Cruzamiento sucesivo a parental recurrente para transferencia de genes específicos."
      },
      {
        descripcion: "Híbridos F1: En autógamas, desarrollo ocasional de híbridos aprovechando heterosis."
      }
    ],
    ejemploAplicado: "Programa de mejoramiento en trigo (Triticum aestivum, autógama) para resistencia a roya del tallo (Puccinia graminis). Se cruzó cultivar comercial sensible (INTA Bagó) con accesión resistente (Pi no. 644788 de CIMMYT). Usando método pedigrí, se seleccionaron 200 plantas en F2, evaluadas en F3 para reacción de roya. Se llevaron 50 líneas élites a F4, y evaluaciones en F5-F6 confirmaron 10 líneas con resistencia durable y rendimiento > 4.5 ton/ha. En F7 (2027), se lanzaron tres cultivares INTA-Trigo-2027-R1, R2 y R3. Este programa de 7 años permitió desarrollar líneas puras homocigotas con resistencia durable transferida desde germoplasma silvestre."
  },
  {
    numero: 12,
    titulo: "Mejoramiento en Asexuales",
    resumen: "En plantas de reproducción asexual, el mejoramiento se centra en la perpetuación de genotipos heterocigotos superiores mediante clonación. Esta unidad examina métodos de selección clonal, hibridación seguida de clonación, apomixis, y saneamiento de germoplasma.",
    conceptosClave: [
      {
        descripcion: "Selección clonal: Identificación y multiplicación de genotipos superiores por propagación vegetativa."
      },
      {
        descripcion: "Hibridación y clonación: Desarrollo de híbridos por vía sexual seguido de clonación del mejor genotipo."
      },
      {
        descripcion: "Apomixis: Reproducción asexual por semilla, mantiene heterocigosis sin necesidad de clonación."
      },
      {
        descripcion: "Saneamiento de germoplasma: Eliminación de virus y patógenos mediante cultivo de tejidos y termoterapia."
      },
      {
        descripcion: "Citoplasma Male Sterile (CMS): Sistema de esterilidad masculina citoplásmica para producción de semilla híbrida."
      }
    ],
    ejemploAplicado: "Programa de mejoramiento de papa (Solanum tuberosum) para resistencia a Phytophthora infestans. Se evaluó germoplasma identificando clon M-2 con resistencia alta pero rendimiento bajo (12 ton/ha) versus variedad Spunta (18 ton/ha). Se cruzó M-2 (hembra) × Spunta (macho), generando 200 plantas F1. Se seleccionó clon CIP-Resistente-2026 con resistencia de M-2 (reacción R2) y rendimiento mejorado (16 ton/ha). Sistema CMS facilitó producción de semilla híbrida certificada. Este enfoque combina hibridación sexual para generar variabilidad con clonación para perpetuar el genotipo heterocigoto superior."
  },
  {
    numero: 13,
    titulo: "Interacción G×A",
    resumen: "La respuesta diferencial de genotipos a diferentes ambientes es la interacción G×A. Esta unidad examina componentes de variación en multi-ambientes, análisis de varianza factorial, regresión conjunta, modelos AMMI y GGE Biplot para evaluar estabilidad fenotípica.",
    conceptosClave: [
      {
        descripcion: "Variación G×A: Cambios en ranking de genotipos entre ambientes indica interacción significativa."
      },
      {
        descripcion: "ANOVA factorial: Descomposición de varianza en efectos genotipo, ambiente e interacción G×A."
      },
      {
        descripcion: "Regresión conjunta (Eberhart & Russell): Parámetros bi (pendiente) y s²di (desviación) para estabilidad."
      },
      {
        descripcion: "Modelo AMMI: Additive Main effects and Multiplicative Interactions análisis biplot."
      },
      {
        descripcion: "GGE Biplot: Genotype-by-Environment biplot visualización de estabilidad y rendimiento."
      },
      {
        descripcion: "Estabilidad dinámica: Genotipos que responden positivamente a mejora ambiental."
      }
    ],
    ejemploAplicado: "Programa nacional de evaluación de soja (Glycine max): 12 cultivares en 8 ambientes (4 años × 2 localidades: Buenos Aires y Córdoba). Rendimiento promedio 3.5 ton/ha. Cultivar A (rendimiento 3.8 ton/ha, bi=0.85) mostró estabilidad. Cultivar B (3.2 ton/ha, bi=1.45) fue responsivo a ambientes favorables. GGE Biplot identificó Cultivar C (3.9 ton/ha) como ideal con baja interacción. Recomendaciones: Cultivar B para ambientes favorables (riego, buena nutrición), Cultivar A para ambientes variables, Cultivar C para amplitud de adaptación. La consideración de G×A es crítica para recomendaciones regionales efectivas."
  },
  {
    numero: 14,
    titulo: "Producción de Semilla",
    resumen: "La producción de semilla de calidad es fundamental para que nuevos cultivares lleguen a productores. Esta unidad examina el marco legal de la semilla, categorías de semillas, métodos de producción específicos para híbridos, e instituciones regulatorias como INASE.",
    conceptosClave: [
      {
        descripcion: "Ley 20.247: Marco legal argentino para semillas que regula producción, comercialización y calidad."
      },
      {
        descripcion: "INASE: Instituto Nacional de Semillas, autoridad regulatoria en Argentina."
      },
      {
        descripcion: "Clases de semilla: Semilla genética (FG, élite), básica (B), certificada (C1, C2)."
      },
      {
        descripcion: "Producción de semilla híbrida: Sistemas de CMS (Cytoplasmic Male Sterility), restauradores de fertilidad (Rf)."
      },
      {
        descripcion: "Derechos del obtentor (UPOV): Protección de cultivares, sistema UPOV 1978/91 para reproducción varietal."
      },
      {
        descripcion: "Sanidad de semilla: Control de patógenos, plagas y pureza varietal."
      }
    ],
    ejemploAplicado: "Producción de semilla híbrida de girasol (Helianthus annuus) usando sistema CMS. Línea CMS-03 (macho-estéril) cruzada con restaurador Rf-línea-B genera F1 híbrido. Producción en 1000 hectáreas con aislamiento de 800 metros, proporción madre:restaurador 8:2. Control de plantas fuera de tipo. Rendimiento 1.8 ton/ha de semilla con puridad genética 99.2% y germinación 92%. Clasificada como Semilla Básica según INASE. Este sistema permitió producción eficiente de semilla F1 híbrida manteniendo heterosis sin necesidad de emasculación manual."
  },
  {
    numero: 15,
    titulo: "Biotecnología",
    resumen: "Las herramientas biotecnológicas son complementarias al mejoramiento convencional, permitiendo superar barreras de incompatibilidad reproductiva y acelerar procesos de selección. Esta unidad examina técnicas de cultivo de tejidos, transformación genética, marcadores moleculares y edición genética.",
    conceptosClave: [
      {
        descripcion: "Cultivo de tejidos: Regeneración de plantas desde callo, generación de variación somaclonal."
      },
      {
        descripcion: "Hibridación interespecífica: Cruzamientos entre especies distantes seguida de rescate de embrión."
      },
      {
        descripcion: "Fusión de protoplastos: Combinación de genomas completos de dos especies."
      },
      {
        descripcion: "Doble-haploides (DH): Producción de líneas homocigotas en una generación mediante cultivo de anteras."
      },
      {
        descripcion: "Transformación genética: Introducción de genes vía Agrobacterium tumefaciens o biobalística."
      },
      {
        descripcion: "Marcadores moleculares (MAS): Selección asistida por marcadores para caracteres de baja heredabilidad."
      },
      {
        descripcion: "CRISPR/Cas9: Edición genética precisa para ganancia de función o pérdida de función."
      }
    ],
    ejemploAplicado: "Programa integrado de mejoramiento en maíz para tolerancia a sequía con gen Bt. Doble-haploides (DH) redujeron tiempo de homocigosis de 6-7 años a 2-3 años. QTL de tolerancia a sequía identificado mediante MAS (marcador SNP). Transformación genética con Agrobacterium introdujo gen cry1Ab (Bt) para resistencia a Ostrinia. La línea final tolera 40% déficit hídrico manteniendo 80% rendimiento (vs cultivar sensible que pierde 50%). Reducción de tiempo total de desarrollo de 12 años (convencional) a 5 años (integrado), demostrando sinergias entre biotecnología y mejoramiento tradicional."
  }
];
